import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Paperclip, Send, X, ArrowRight, User, Building2 } from "lucide-react";

const ALLOWED = ["application/pdf", "image/png", "image/jpeg", "image/webp", "text/plain"];
const MAX_BYTES = 2 * 1024 * 1024;

export default function Contact() {
  const { role, commerce } = useAuth();
  const nav = useNavigate();

  // Authenticated establishment (and admins logged in) keep their full
  // tracking experience untouched — the form submits with their account
  // and the redirect to /my-requests/<id> lands them on the conversation page.
  const isEstablishment = role === "commerce";

  // For everyone else (anonymous visitors, guest token, non-merchant clients),
  // we now require an explicit role choice before showing the form. Visitors
  // get the simplified flow (no in-app tracking, email-only follow-up); users
  // who declare themselves as an establishment are redirected to the
  // establishment login so they can submit with their account.
  // pickedRole: null | "visitor" | "establishment"
  const [pickedRole, setPickedRole] = useState(isEstablishment ? "establishment" : null);

  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [loading, setLoading] = useState(false);

  const pickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!ALLOWED.includes(f.type)) {
      toast.error("Type de fichier non autorisé");
      e.target.value = "";
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error("Fichier trop volumineux (max 2 Mo)");
      e.target.value = "";
      return;
    }
    const b64 = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result).split(",")[1] || "");
      r.onerror = reject;
      r.readAsDataURL(f);
    });
    setAttachment({ name: f.name, mime: f.type, size: f.size, dataBase64: b64 });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (subject.trim().length < 3) return toast.error("Objet trop court");
    if (message.trim().length < 10) return toast.error("Message trop court (10 caractères min.)");

    const asVisitor = pickedRole === "visitor";
    if (asVisitor) {
      const trimmed = contactEmail.trim();
      const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
      if (!emailOk) return toast.error("Veuillez saisir une adresse email valide pour recevoir la réponse.");
    }

    setLoading(true);
    try {
      const payload = { subject, message, attachment };
      if (asVisitor) payload.contactEmail = contactEmail.trim();
      const { data } = await api.post("/support/tickets", payload);

      if (isEstablishment) {
        // Establishment keeps the full tracking experience.
        toast.success(`Votre demande a bien été envoyée — Numéro : ${data.trackingNumber}`, { duration: 6000 });
        nav(`/my-requests/${data.id}`);
      } else {
        // Visitors get an email-only follow-up — no in-app tracking page.
        toast.success(
          `Votre demande a bien été envoyée — Numéro : ${data.trackingNumber}. La réponse du support vous sera transmise par email.`,
          { duration: 8000 },
        );
        nav("/");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  // ---------- Role chooser (visitors only) ----------
  if (!isEstablishment && pickedRole === null) {
    return (
      <div className="container mx-auto px-4 md:px-8 py-12 md:py-16 max-w-2xl" data-testid="contact-role-chooser">
        <p className="overline mb-2">Support</p>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-ink">Contacter le support</h1>
        <p className="mt-3 text-subtle">
          Avant d'envoyer votre demande, indiquez-nous qui vous êtes pour que nous puissions vous répondre correctement.
        </p>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => setPickedRole("visitor")}
            className="group text-left border-2 border-ink rounded-xl bg-white p-6 hover:bg-ink hover:text-white transition-colors shadow-sm"
            data-testid="contact-role-visitor-btn"
          >
            <div className="flex items-center gap-3">
              <span className="inline-flex w-11 h-11 rounded-full border-2 border-ink group-hover:border-white items-center justify-center">
                <User size={22} />
              </span>
              <span className="overline">Choix</span>
            </div>
            <p className="mt-5 font-display text-2xl font-black tracking-tight leading-tight">
              Je suis un visiteur
            </p>
            <p className="mt-2 text-sm text-subtle group-hover:text-white/80 leading-snug">
              Vous prenez des tickets sans compte. La réponse vous sera envoyée par email.
            </p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-brand group-hover:text-white">
              Continuer <ArrowRight size={14} />
            </span>
          </button>

          <button
            type="button"
            onClick={() => nav("/commerce/login?next=/contact")}
            className="group text-left border-2 border-ink rounded-xl bg-white p-6 hover:bg-ink hover:text-white transition-colors shadow-sm"
            data-testid="contact-role-establishment-btn"
          >
            <div className="flex items-center gap-3">
              <span className="inline-flex w-11 h-11 rounded-full border-2 border-ink group-hover:border-white items-center justify-center">
                <Building2 size={22} />
              </span>
              <span className="overline">Choix</span>
            </div>
            <p className="mt-5 font-display text-2xl font-black tracking-tight leading-tight">
              Je suis un établissement
            </p>
            <p className="mt-2 text-sm text-subtle group-hover:text-white/80 leading-snug">
              Connectez-vous à votre espace pour suivre vos demandes depuis « Mes demandes ».
            </p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-brand group-hover:text-white">
              Se connecter <ArrowRight size={14} />
            </span>
          </button>
        </div>
      </div>
    );
  }

  // ---------- Form (visitor with picked role, or authenticated establishment) ----------
  return (
    <div className="container mx-auto px-4 md:px-8 py-12 md:py-16 max-w-2xl">
      <p className="overline mb-2">Support</p>
      <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-ink">Contacter le support</h1>
      <p className="mt-3 text-subtle">
        {isEstablishment ? (
          <>Envoyez-nous votre demande. Nous vous répondrons dans les meilleurs délais. Vous pourrez suivre votre demande dans <strong>Mes demandes</strong>.</>
        ) : (
          <>Envoyez-nous votre demande. La réponse du support vous sera transmise par email.</>
        )}
      </p>

      {isEstablishment ? (
        <div className="mt-6 border border-border p-4 bg-surface/50 text-sm text-subtle">
          Connecté en tant qu'<strong className="text-ink">{commerce?.name}</strong> · Établissement
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setPickedRole(null)}
          className="mt-6 text-xs text-subtle hover:text-ink inline-flex items-center gap-1"
          data-testid="contact-role-change-link"
        >
          ← Modifier votre choix
        </button>
      )}

      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="contact-form">
        {pickedRole === "visitor" ? (
          <div data-testid="guest-email-block">
            <Label htmlFor="contact-email-input">
              Votre adresse email <span className="text-destructive">*</span>
            </Label>
            <Input
              id="contact-email-input"
              required
              type="email"
              autoComplete="email"
              inputMode="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              maxLength={180}
              className="h-12 mt-1.5"
              placeholder="exemple@email.com"
              data-testid="contact-email-input"
            />
            <p className="text-xs text-subtle mt-1.5 leading-snug">
              C'est sur cette adresse que le support vous enverra sa réponse.
            </p>
          </div>
        ) : null}
        <div>
          <Label>Objet</Label>
          <Input required value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={180} className="h-12 mt-1.5" placeholder="Ex : Problème de paiement, question sur la file..." data-testid="subject-input" />
        </div>
        <div>
          <Label>Message</Label>
          <Textarea required value={message} onChange={(e) => setMessage(e.target.value)} maxLength={5000} rows={7} className="mt-1.5 resize-none" placeholder="Décrivez votre demande en détail..." data-testid="message-input" />
          <p className="text-xs text-subtle mt-1">{message.length}/5000</p>
        </div>
        <div>
          <Label>Pièce jointe <span className="text-subtle font-normal">(facultatif · max 2 Mo)</span></Label>
          {!attachment ? (
            <label className="mt-1.5 flex items-center gap-2 h-12 px-4 border border-dashed border-border cursor-pointer hover:bg-surface" data-testid="file-label">
              <Paperclip size={16} className="text-subtle" />
              <span className="text-sm text-subtle">Ajouter un fichier (PDF, image, texte)</span>
              <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.txt" onChange={pickFile} data-testid="file-input" />
            </label>
          ) : (
            <div className="mt-1.5 flex items-center gap-2 h-12 px-4 border border-border bg-surface/60" data-testid="attachment-preview">
              <Paperclip size={16} className="text-brand" />
              <span className="text-sm text-ink flex-1 truncate">{attachment.name}</span>
              <span className="text-xs text-subtle">{Math.round(attachment.size / 1024)} Ko</span>
              <button type="button" onClick={() => setAttachment(null)} className="text-subtle hover:text-destructive" aria-label="Retirer">
                <X size={16} />
              </button>
            </div>
          )}
        </div>

        <Button type="submit" disabled={loading} className="w-full h-14 bg-brand hover:bg-brand-hover text-white rounded-md font-medium text-base" data-testid="submit-btn">
          {loading ? "Envoi…" : <><Send size={16} className="mr-2" /> Envoyer ma demande</>}
        </Button>
      </form>
    </div>
  );
}
